// ── API Endpoint Constants ──
// Routes map to the actual .NET 8 backend controllers.
// Vite proxy rewrites /api/* → backend.
// Comments show: HTTP method, parameter format, backend DTO name.
// Endpoints marked TODO have no backend implementation yet.

const API = '/api' as const;

export const Endpoints = {
  access: {
    psls: `${API}/Access/psls`,
    permissions: `${API}/Access/permissions`,
    roles: `${API}/Access/roles`,
    role: (roleId: string) => `${API}/Access/roles/${roleId}`,
    userAssignments: (userId: string) => `${API}/Access/users/${userId}/assignments`,
    userRoleAssignments: (userId: string, roleId: string) => `${API}/Access/users/${userId}/assignments/${roleId}`,
    check: `${API}/Access/check`,
    audit: `${API}/Access/audit`,
  },
  // ══════════════════════════════════════════════════════
  // AuthController → [Route("api/Auth")]
  // ══════════════════════════════════════════════════════
  auth: {
    /** POST body: LoginDTO { UserName, Password, Portal } */
    login: `${API}/Auth/login`,
    /** POST — revokes the current bearer token */
    logout: `${API}/Auth/logout`,
    /** GET — current identity + aspnet_Roles memberships */
    me: `${API}/Auth/me`,
    /** POST body: RegisterVendorUserDTO — vendor portal sign-up */
    registerVendor: `${API}/Auth/register/vendor`,
    /** POST body: RequestAccessDTO — internal user access request */
    requestAccess: `${API}/Auth/request-access`,
    /** POST body: ApproveAccessRequestDTO — admin approval */
    approveAccess: `${API}/Auth/approve-access`,
    /** POST body: ChangePasswordDTO */
    changePassword: `${API}/Auth/change-password`,
    /** POST body: ForgotPasswordDTO → returns the security question */
    forgotPassword: `${API}/Auth/forgot-password`,
    /** POST body: ResetPasswordDTO */
    resetPassword: `${API}/Auth/reset-password`,
  },

  // ══════════════════════════════════════════════════════
  // ProductController → [Route("api/Product")]
  // ══════════════════════════════════════════════════════
  products: {
    /** POST body: ProductPagingDTO { StartIndex, MaximumRows, SortBy, ProdName, PslId, Statuses... } */
    list: `${API}/Product/getProdWithPaging`,
    /** GET ?prodId={Guid} */
    byId: `${API}/Product/getProdById`,
    /** POST body: AddProductDTO */
    create: `${API}/Product/addProd`,
    /** PUT body: UpdateNotesDTO { ProdId, Notes, CurrentUser } — NOTE: backend has no single unified update; use specific update* endpoints */
    updateNotes: `${API}/Product/updateNotes`,
    /** PUT body: UpdateProductDTO { Oid, ProductSAPNumber, Name, ... } — updates Section I basic product fields */
    updateBasicInfo: `${API}/Product/updateBasicInfo`,
    /** PUT body: UpdateNotesDTO */
    updateFormulaNotes: `${API}/Product/updateFormulaNotes`,
    /** PUT body: UpdatePackagingDTO */
    updatePackaging: `${API}/Product/updatePackaging`,
    /** PUT ?prodId=&MDescription=&currentUser= */
    updateMDescription: `${API}/Product/updateMDescription`,
    /** PUT ?prodId=&typicalProperties=&currentUser= */
    updateTypicalProp: `${API}/Product/updateTypicalProp`,
    /** PUT ?prodId=&storagePrecaution=&currentUser= */
    updateStoragePrecaution: `${API}/Product/updateStoragePrecaution`,
    /** PUT body: UpdateTranRegulationsDTO */
    updateTranRegulations: `${API}/Product/updateTranRegulations`,
    /** PUT ?prodId=&history=&currentUser= */
    updateHistory: `${API}/Product/updateHistory`,
    /** DELETE ?prodId={Guid} */
    delete: `${API}/Product`,
    /** PUT ?currentUser={string}&prodId={Guid} */
    duplicate: `${API}/Product/duplicateProd`,
    /** PUT ?currentUser={string}&prodId={Guid} */
    approve: `${API}/Product/approveProd`,
    /** PUT ?currentUser={string}&prodId={Guid} */
    deactivate: `${API}/Product/deactivateProd`,
    /** PUT ?currentUser={string}&prodId={Guid} */
    reactivate: `${API}/Product/reactivateProd`,
    /** POST body: ProductFilterDTO → returns count */
    count: `${API}/Product/count`,
    /** GET — all products */
    getAll: `${API}/Product`,
    /** GET ?PSL={string} */
    getAllByPSL: `${API}/Product/getAllProdbyPSL`,
    /** GET — active products only */
    active: `${API}/Product/activeProd`,
    /** GET ?PSL={string} */
    activeByPSL: `${API}/Product/activeProdByPSL`,
    /** GET ?fullName={string} */
    getByFullName: `${API}/Product/getProdByFullName`,
    /** GET ?prodName={string} → returns product ID */
    getProdId: `${API}/Product/getProdId`,
    /** GET ?productId={Guid} → returns PSL ID */
    getPSLId: `${API}/Product/getPSLId`,
    /** GET ?prodId={Guid} */
    hasBeenSuperceded: `${API}/Product/hasBeenSuperceded`,
    /** GET ?prodId={Guid} */
    pendingProd: `${API}/Product/pendingProd`,
    /** GET ?ProductId={Guid}&ProdcutName={string}&PslId={string} */
    getApprovedSuppliers: `${API}/Product/getApprovedSuppliers`,
    /** GET same params as getApprovedSuppliers */
    getApprovedVQCASuppliers: `${API}/Product/getApprovedVQCASuppliers`,
    /** GET ?ProductId={Guid}&ProductName={string} — COA lot count per vendor */
    getCOALotCount: `${API}/Product/getCOALotCount`,
    /** GET /api/Product/audit/{productId} — product change-audit trail */
    audit: (productId: string) => `${API}/Product/audit/${productId}`,
    /** GET ?supercedeId={Guid} */
    getBySupercedeId: `${API}/Product/getProdBySupercedeId`,
    /** GET ?prodId={Guid} */
    getAllSuperceded: `${API}/Product/getAllSuperceded`,
    /** GET ?prodId={Guid} — has this product superceded another? */
    supercededProd: `${API}/Product/supercededProd`,
    /** GET ?prodId={Guid} — full chain: all older products superceded by this one */
    getAllSupercededThis: `${API}/Product/getAllSupercededThis`,
    /** GET ?prodId={Guid} — chain downward (older products) with Name/CreatedDate/StatusName */
    supercedingProdById: `${API}/Product/supercedingProdById`,
    /** GET ?prodId={Guid} — chain upward (newer products) with Name/CreatedDate/StatusName */
    supercingProdById: `${API}/Product/supercingProdById`,
  },

  // ══════════════════════════════════════════════════════
  // FormulaController → [Route("api/Formula")]
  // ══════════════════════════════════════════════════════
  formulas: {
    /** GET ?ProdId={Guid} */
    byProductId: `${API}/Formula/GetByProdId`,
    /** GET api/Formula/{Oid} */
    byId: (oid: string) => `${API}/Formula/${oid}`,
    /** POST body: FormulaDTO */
    add: `${API}/Formula/Add`,
    /** DELETE api/Formula/{Oid} */
    delete: (oid: string) => `${API}/Formula/${oid}`,
  },

  // ══════════════════════════════════════════════════════
  // ProdApprovalController → [Route("api/ProdApproval")]
  // ══════════════════════════════════════════════════════
  prodApprovals: {
    /** GET ?ProdId={Guid} */
    byProdId: `${API}/ProdApproval/byProdId`,
    /** GET ?Oid={Guid} */
    byId: `${API}/ProdApproval/byId`,
    /** POST body: ProdApprovalDTO */
    create: `${API}/ProdApproval`,
    /** DELETE ?Oid={Guid} */
    delete: `${API}/ProdApproval`,
  },

  // ══════════════════════════════════════════════════════
  // ApprovedProdSupplierController → [Route("api/ApprovedProdSupplier")]
  // ══════════════════════════════════════════════════════
  approvedSuppliers: {
    /** POST body: AddApprovedProdSupplierDTO */
    create: `${API}/ApprovedProdSupplier`,
  },

  // ══════════════════════════════════════════════════════
  // STPController → [Route("api/STP")]
  // ══════════════════════════════════════════════════════
  stp: {
    /** POST body: STPDTO { STPId?, STPName?, STPNumber?, Statuses? } */
    list: `${API}/STP/GetAll`,
    /** GET ?sTPId={Guid} */
    byId: `${API}/STP/byId`,
    /** GET ?sTPNumber={string} */
    byNumber: `${API}/STP/byNumber`,
    /** GET ?fullSTPName={string} */
    byFullName: `${API}/STP/byFullName`,
    /** POST body: AddSTPDTO */
    create: `${API}/STP`,
    /** PUT body: UpdateSTPDTO */
    update: `${API}/STP`,
    /** DELETE ?originalOid={Guid} */
    delete: `${API}/STP`,
    /** GET ?oId={Guid}&newOId={Guid}&curUser={string} */
    supersede: `${API}/STP/Supercede`,
    /** PUT ?oId=&stat=&currentUser= */
    updateStatus: `${API}/STP/UpdateStatus`,
    /** GET ?oId=&user= */
    approve: `${API}/STP/approve`,
    /** PUT ?oId=&user= */
    deactivate: `${API}/STP/deactivate`,
    /** PUT ?oId=&user= (route: hasExistingPending) */
    reactivate: `${API}/STP/hasExistingPending`,
    /** PUT ?originalOid=&currentUser= */
    duplicate: `${API}/STP/duplicate`,
    /** GET ?oId={Guid} → chain of STPs this one supersedes */
    superseded: `${API}/STP/superceded`,
    /** GET ?oId={Guid} */
    hasBeenSuperceded: `${API}/STP/hasBeenSupercededByOid`,
    /** GET ?oId={Guid} */
    hasExistingPending: `${API}/STP/hasExistingPending`,
  },

  // ══════════════════════════════════════════════════════
  // VQCAController → [Route("api/VQCA")]
  // ══════════════════════════════════════════════════════
  vqca: {
    /** POST body: VQCADTO filters → VQCAResponse[] */
    list: `${API}/VQCA/search`,
    /** POST body: VQCADTO filters + StartIndex/MaximumRows/SortBy → { items, totalCount } */
    listPaged: `${API}/VQCA/search/paged`,
    /** GET api/VQCA/details/{Oid}?vqcaId={Guid} */
    byId: (oid: string) => `${API}/VQCA/details/${oid}`,
    /** GET ?vendorId={Guid} */
    byVendorQuery: `${API}/VQCA`,
    /** GET api/VQCA/vendor/{vendorId} */
    byVendor: (vendorId: string) => `${API}/VQCA/vendor/${vendorId}`,
    /** GET ?applicationName=&userName= → returns vendor ID */
    getVendorId: `${API}/VQCA/get-vendorid`,
    /** GET ?VQCAId={Guid}&SignedBy={Guid?}&OID={Guid?} */
    signLogs: `${API}/VQCA/get-signlog`,
    create: `${API}/VQCA`,
    signGql: `${API}/VQCA/sign-gql`,
    signCm: `${API}/VQCA/sign-cm`,
    rejectGql: `${API}/VQCA/reject-gql`,
    rejectCm: `${API}/VQCA/reject-cm`,
    deactivate: `${API}/VQCA/deactivate`,
    supersede: `${API}/VQCA/supersede`,
    fromRequest: (requestId: string) => `${API}/VQCA/from-request/${requestId}`,
  },

  // ══════════════════════════════════════════════════════
  // VQCAReqController → [Route("api/VQCAReq")]
  // ══════════════════════════════════════════════════════
  vendorRequests: {
    /** GET — all vendor requests */
    list: `${API}/VQCAReq`,
    /** POST body: VendorReqDTO filters + StartIndex/MaximumRows/SortBy → { items, totalCount } */
    searchPaged: `${API}/VQCAReq/search/paged`,
    /** POST body: VendorReqDTO */
    create: `${API}/VQCAReq`,
    /** PUT body: UpdateDTO */
    update: `${API}/VQCAReq`,
    /** PUT ?Oid={Guid}&ProductId={Guid} */
    updateProduct: `${API}/VQCAReq/reqProduct`,
    byId: (oid: string) => `${API}/VQCAReq/byId/${oid}`,
    status: (oid: string) => `${API}/VQCAReq/status/${oid}`,
    delete: (oid: string) => `${API}/VQCAReq/${oid}`,
  },

  // ══════════════════════════════════════════════════════
  // CoaController → [Route("api/COA")]
  // ══════════════════════════════════════════════════════
  coa: {
    /** POST body: GetCOADTO { OID?, DtFrom?, DtTo?, ProductId?, VendorId?, LotNumber?, Statuses?, PslId?, StartIndex?, MaximumRows?, SORTBY? } */
    list: `${API}/COA/all`,
    /** POST body: GetCOADTO — same filters as `list`, un-paged, export columns only */
    listExport: `${API}/COA/all/export`,
    /** GET api/COA/{coaId} */
    byId: (coaId: string) => `${API}/COA/${coaId}`,
    /** POST body: AddCOADTO { ProductId, VendorId, VQCAId, LotNumber, IssueDate, ProductionDate, Status, Notes, CreatedBy } */
    create: `${API}/COA/AddCOA`,
    /** POST body: AddVendorCOADTO — COA + PO numbers + experiment + analysis results in one call */
    submit: `${API}/COA/AddVendorCOA`,
    /** POST api/COA/{coaId}/results body: AddCoaResultsDTO — another analysis run on an existing COA */
    addResults: (coaId: string) => `${API}/COA/${coaId}/results`,
    /** POST body: GetCOACountDTO → returns count */
    count: `${API}/COA/count`,
    /** POST ?coaId={Guid}&status={COAStatus}&user={string} */
    approve: `${API}/COA/PassCOA`,
    /** POST api/COA/setFailedStatus/{coaId} */
    reject: (coaId: string) => `${API}/COA/setFailedStatus/${coaId}`,
    /** DELETE ?coaId={string} (comma-separated GUIDs) */
    delete: `${API}/COA/DeleteSelectedCOA`,
    /** GET ?coaId={string} → un-delete */
    unDelete: `${API}/COA/UnDeleteSelectedCOA`,
    /** POST body: AddCOANotesDTO { CoaId, Notes, CurUser } */
    updateNotes: `${API}/COA/UpdateCOANotes`,
    /** PUT api/COA/{coaId} body: UpdateCOADTO { LotNum, ProdDate, Notes, CurrentUser } */
    update: (coaId: string) => `${API}/COA/${coaId}`,
    /** GET ?lotNum=&vqcaId= → check duplicate */
    duplicateExists: `${API}/COA/DuplicateCOAExists`,
    /** GET ?coaId={Guid} */
    getNewSpecsById: `${API}/COA/GetCOANewSpecsById`,
    /** GET ?coaId={Guid} → PO numbers for a COA */
    poByCoa: `${API}/COA/GetPOByCOA`,
    /** POST body: AddPONumberDTO { VendorId, CoaId, PoNumber, CurUser } */
    addPo: `${API}/COA/AddPONumber`,
    /** DELETE api/COA/DeletePONumber/{poId} */
    deletePo: (poId: string) => `${API}/COA/DeletePONumber/${poId}`,
    /** POST body: COAByVendorDTO */
    getByVendor: `${API}/COA/GetCOAByVendor`,
    /** POST body: COAByVendorDTO */
    getByVendorList: `${API}/COA/GetCOAByVendorList`,
    /** GET ?coaId={Guid} */
    getPerfSpecByCOA: `${API}/COA/GetPerfSpecByCOA`,
    /** GET api/COA/GetFailedPerfExperiment/{coaId} */
    getFailedPerfExperiment: (coaId: string) => `${API}/COA/GetFailedPerfExperiment/${coaId}`,
    /** GET api/COA/GetPerfExpCountByCOA/{coaId} */
    getPerfExpCount: (coaId: string) => `${API}/COA/GetPerfExpCountByCOA/${coaId}`,
  },

  // ══════════════════════════════════════════════════════
  // COAAttachmentController → [Route("api/COAAttachment")]
  // ══════════════════════════════════════════════════════
  coaAttachments: {
    /** GET api/COAAttachment/{oid} */
    byId: (oid: string) => `${API}/COAAttachment/${oid}`,
    /** GET ?coaId={Guid} */
    byCOA: `${API}/COAAttachment/GetByCOA`,
    /** GET ?coaId={Guid}&siteName={string} */
    vendor: `${API}/COAAttachment/vendor`,
    /** DELETE api/COAAttachment/{oid} */
    delete: (oid: string) => `${API}/COAAttachment/${oid}`,
    /** POST body: COAAttachmentDTO { DocName, DocType, DocData, CoaId, CreatedBy, SiteName } */
    create: `${API}/COAAttachment/coa`,
  },

  // ══════════════════════════════════════════════════════
  // COAExperimentController → [Route("api/COAExperiment")]
  // ══════════════════════════════════════════════════════
  coaExperiments: {
    /** GET api/COAExperiment/{expId} */
    byId: (expId: string) => `${API}/COAExperiment/${expId}`,
    /** GET ?coaId={Guid} */
    byCOA: `${API}/COAExperiment/GetByCOA`,
    /** GET ?statuses=&COAStatuses= */
    byStatuses: `${API}/COAExperiment/GetByStatuses`,
    /** POST body: AddCOAExperimentDTO */
    create: `${API}/COAExperiment`,
  },

  // ══════════════════════════════════════════════════════
  // COAResultController → [Route("api/COAResult")]
  // ══════════════════════════════════════════════════════
  coaResults: {
    /** GET ?expId={Guid}&COAId={Guid} */
    perfDetails: `${API}/COAResult/COAPerfDetails`,
    /** GET ?Oid={Guid}&expId={Guid}&COAId={Guid} */
    get: `${API}/COAResult`,
    /** GET ?Oid=&expId=&COAId= */
    getByCOAId: `${API}/COAResult/GetByCOAId`,
    /** GET body: COAResultDTO — NOTE: GET with body may not work */
    add: `${API}/COAResult/Add`,
    /** GET body: COAResultDTO — NOTE: GET with body may not work */
    addPerfSpec: `${API}/COAResult/AddXMLCOAPerfSpec`,
  },

  // ══════════════════════════════════════════════════════
  // CoqController → [Route("api/COQ")]
  // ══════════════════════════════════════════════════════
  coq: {
    /** POST body: GetCOQRequestDTO { ProdId?, LotNumber?, ProdName?, DtFrom?, DtTo?, Statuses?, StartIndex, MaximumRows, SortExpression? } */
    list: `${API}/COQ/COQCustomPager`,
    /** POST body: COQCountRequestDTO → returns count */
    count: `${API}/COQ/COQCount`,
    /** GET ?COQId={Guid} */
    byId: `${API}/COQ/GetCOQbyId`,
    /** GET ?COQNumber={int} */
    byNumber: `${API}/COQ/GetCOQbyNumber`,
    /** GET ?LotNumber={string} */
    byLot: `${API}/COQ/GetCOQbyLot`,
    /** GET — all COQs */
    getAll: `${API}/COQ/GetCOQs`,
    /** POST body: InsCOQDTO */
    create: `${API}/COQ/InsCOQbyId`,
    /** POST body: CoqLotSearchDTO { LotNumber?, PONumber?, ProdName? } → CoqLotSearchResponse */
    lotSearch: `${API}/COQ/LotSearch`,
    /** POST body: CoqGenerateContextDTO { Mode, SourceId, LotNumber? } → CoqGenerateContextResponse */
    generateContext: `${API}/COQ/GenerateContext`,
    /** POST body: GenerateCOQDTO → GenerateCOQResponse */
    generate: `${API}/COQ/Generate`,
    /** GET api/COQ/{coqId}/PONumbers */
    poNumbers: (coqId: string) => `${API}/COQ/${coqId}/PONumbers`,
    /** DELETE api/COQ/PONumbers/{poId} */
    deletePoNumber: (poId: string) => `${API}/COQ/PONumbers/${poId}`,
    /** POST body: SaleSpecsDTO { ProductId, PslId? } → SaleSpecsReportResponse */
    saleSpecs: `${API}/COQ/SaleSpecs`,
    /** DELETE ?COQNumber={string} */
    delete: `${API}/COQ`,
    /** POST ?COQNumbers={string} → soft-delete */
    deleteSelected: `${API}/COQ/DeleteCOQs`,
    /** POST ?COQNumbers={string} → un-delete */
    unDeleteSelected: `${API}/COQ/UnDeleteCOQs`,
    /** GET ?vendorList={string} */
    vendorUserIds: `${API}/COQ/VendorUserIds`,
    /** GET ?LotNumber=&ProductName= */
    coqData: `${API}/COQ/COQData`,
    /** GET ?COQId={Guid} → vendor report */
    vendorRpt: `${API}/COQ/GetCOQVendorRpt`,
    /** GET ?COQId={Guid} → specs vendor report */
    specsVendorRpt: `${API}/COQ/GetCOQSpecsVendorRpt`,
    /** GET ?Vendor=&ProdName=&DtFrom=&DtTo= */
    siteSearch: `${API}/COQ/GetCOQSiteSearch`,
  },

  // ══════════════════════════════════════════════════════
  // COQSpecsController → [Route("api/COQSpecs")]
  // ══════════════════════════════════════════════════════
  coqSpecs: {
    /** GET api/COQSpecs/GetCOQSpecs/{COQId} */
    byCOQ: (coqId: string) => `${API}/COQSpecs/GetCOQSpecs/${coqId}`,
  },

  // ══════════════════════════════════════════════════════
  // SampleController → [Route("api/Sample")]
  // ══════════════════════════════════════════════════════
  samples: {
    /** POST body: SampleFilterDTO { ProductName?, LotNumber?, Statuses?, StartIndex, MaximumRows, SORTBY?, Psl? } */
    list: `${API}/Sample/getSampleByPaging`,
    /** GET ?GetSampleById={Guid} */
    byId: `${API}/Sample/getSampleById`,
    /** GET api/Sample/detail/{sampleId} — view page: sample, experiment grid and button flags */
    detail: (sampleId: string) => `${API}/Sample/detail/${sampleId}`,
    productLookup: `${API}/Sample/products`,
    categoryLookup: `${API}/Sample/categories`,
    /** GET ?catLevelId={Guid}&assignDate={ISO} — read-only target date */
    targetDate: `${API}/Sample/targetDate`,
    /** GET ?sourcePerson={name} — can the new-sample alert reach this person, and why not */
    checkSourcePerson: `${API}/Sample/checkSourcePerson`,
    /** POST body: AddSampleDTO → { oid, qaNumber, status, statusName, alertSent, alertMessage } */
    create: `${API}/Sample/addSample`,
    /** PUT body: UpdateSampleDTO */
    update: `${API}/Sample/updateSample`,
    /** POST api/Sample/{sampleId}/reOpen — supervisor only, FAILED or PASSED only */
    reOpenSample: (sampleId: string) => `${API}/Sample/${sampleId}/reOpen`,
    /** PUT ?sampleId={Guid} — the acting user is taken from the token */
    pass: `${API}/Sample/passSample`,
    /** PUT ?sampleId={Guid} — the acting user is taken from the token */
    fail: `${API}/Sample/failSample`,
    /** PUT ?sampleId={Guid} — the acting user is taken from the token */
    inProcess: `${API}/Sample/inProcessSample`,
    /** PUT ?sampleId={Guid} — the acting user is taken from the token */
    reOpen: `${API}/Sample/reOpenSample`,
    /** POST body: SampleCountDTO → returns count */
    count: `${API}/Sample/samplesCount`,
    /** POST — returns all samples */
    getAll: `${API}/Sample/getAllSamples`,
    /** POST body: SampleSiteSearchDTO */
    siteSearch: `${API}/Sample/getSamplesBySiteSearch`,
    /** GET ?sampleId={Guid}&sendAlert={bool} */
    isValidForCOQ: `${API}/Sample/isValidForCOQ`,
    /** POST api/Sample/updateExpStatus/{expId} */
    updateExpStatus: (expId: string) => `${API}/Sample/updateExpStatus/${expId}`,
    /** GET api/Sample/getPerfSpecBySampleExpId/{expId} */
    perfSpecByExpId: (expId: string) => `${API}/Sample/getPerfSpecBySampleExpId/${expId}`,
  },

  // ══════════════════════════════════════════════════════
  // ExperimentController → [Route("api/Experiment")]
  // ══════════════════════════════════════════════════════
  experiments: {
    /** POST ?sampleId={Guid} → experiments for a sample */
    bySampleId: `${API}/Experiment/GetExpBySampleId`,
    /** GET api/Experiment/form/{sampleId} → sample header + both specification grids */
    form: (sampleId: string) => `${API}/Experiment/form/${sampleId}`,
    /** POST body: AddSampleExperimentDTO */
    add: `${API}/Experiment/add`,
    /** GET api/Experiment/detail/{expId} */
    detail: (expId: string) => `${API}/Experiment/detail/${expId}`,
    /** POST api/Experiment/{expId}/fail — body: { notes } */
    fail: (expId: string) => `${API}/Experiment/${expId}/fail`,
    /** POST api/Experiment/{expId}/conditionallyApprove — body: { notes } */
    conditionallyApprove: (expId: string) => `${API}/Experiment/${expId}/conditionallyApprove`,
    /** POST ?Status=&SampleStatues= → experiments by statuses */
    byStatuses: `${API}/Experiment/GetExpByStatuses`,
    /** POST body: PendingFailedExperimentSearchDTO → the supervisor's under-review queue */
    searchPendingFailed: `${API}/Experiment/pendingFailed/search`,
    /** POST — all experiments */
    getAll: `${API}/Experiment/All`,
    /** POST api/Experiment/{Oid} */
    byId: (oid: string) => `${API}/Experiment/${oid}`,
    /** GET ?productName=&vendorId=&fromDate=&toDate= */
    coaExperimentResultSet: `${API}/Experiment/GetCOAExperimentResultSet`,
    /** GET ?productName=&vendorId=&fromDate=&toDate= */
    coaPerfSpecResultSet: `${API}/Experiment/GetCOAPerfSpecResultSet`,
    /** GET ?productName=&vendorId=&fromDate=&toDate=&qaNumber=&status= */
    qaExperimentResultSet: `${API}/Experiment/GetQAExperimentResultSet`,
  },

  // ══════════════════════════════════════════════════════
  // AttachmentController → [Route("api/Attachment")]
  // ══════════════════════════════════════════════════════
  sampleAttachments: {
    /** GET ?QANum={decimal} → attachments by QA number */
    byQa: `${API}/Attachment/GetByQANumber`,
    /** GET api/Attachment/{oid} → download */
    download: (oid: string) => `${API}/Attachment/${oid}`,
    /** POST body: AttachmentDTO { DocName, DocType, DocData, CreatedBy, QANumber } */
    create: `${API}/Attachment`,
    /** DELETE api/Attachment/{oid} */
    delete: (oid: string) => `${API}/Attachment/${oid}`,
  },

  // ── Analysis — TODO: No backend controller yet ──
  analysis: {
    list: `${API}/analysis`,                                          // TODO: not implemented
    byId: (id: string) => `${API}/analysis/${id}`,                    // TODO: not implemented
    create: `${API}/analysis`,                                        // TODO: not implemented
    update: (id: string) => `${API}/analysis/${id}`,                  // TODO: not implemented
    delete: (id: string) => `${API}/analysis/${id}`,                  // TODO: not implemented
    sieves: `${API}/analysis/sieves`,                                 // TODO: not implemented
    sieveById: (id: string) => `${API}/analysis/sieves/${id}`,        // TODO: not implemented
    sieveSets: `${API}/analysis/sieve-sets`,                          // TODO: not implemented
    deleteSieveSet: (id: string) => `${API}/analysis/sieve-sets/${id}`, // TODO: not implemented
  },

  // ══════════════════════════════════════════════════════
  // QAReportController → [Route("api/QAReport")]
  // ══════════════════════════════════════════════════════
  qaReports: {
    /** POST body: QAReportSearchDTO → newest revision of every approved report */
    search: `${API}/QAReport/search`,
    /** POST body: QAReportSearchDTO → newest revision of every report awaiting approval */
    searchUnapproved: `${API}/QAReport/unapproved/search`,
    /** GET api/QAReport/form/{sampleId} → newest revision for a sample */
    form: (sampleId: string) => `${API}/QAReport/form/${sampleId}`,
    /** GET api/QAReport/revision/{qaReportNumber} → one node of the supersede tree */
    revision: (qaReportNumber: number) => `${API}/QAReport/revision/${qaReportNumber}`,
    /** Everything the printed QA report needs. preview prints the newest unapproved revision. */
    document: (qaNumber: number, preview = false) =>
      `${API}/QAReport/document/${qaNumber}${preview ? '?preview=true' : ''}`,
    /** POST body: QAReportSaveDTO → inserts a superseding revision */
    save: `${API}/QAReport/save`,
    /** POST body: QAReportApproveDTO */
    approve: `${API}/QAReport/approve`,
    /** POST api/QAReport/approveTestCertificate/{sampleId} */
    approveTestCertificate: (sampleId: string) =>
      `${API}/QAReport/approveTestCertificate/${sampleId}`,
    /** POST body: QAReportSearchDTO → newest revision of every test-approved report */
    searchTestCertificates: `${API}/QAReport/testCertificates/search`,
    /** POST body: QAReportSearchDTO → every matching approved report, un-paged, for Export to Excel */
    searchExport: `${API}/QAReport/search/export`,
    /** POST body: QAReportSearchDTO → every matching unapproved report, un-paged */
    searchUnapprovedExport: `${API}/QAReport/unapproved/search/export`,
    /** POST body: QAReportSearchDTO → every matching test-approved report, un-paged */
    searchTestCertificatesExport: `${API}/QAReport/testCertificates/search/export`,
    /** GET api/QAReport/certificate/{qaNumber} → the data behind the printed certificate */
    certificate: (qaNumber: number) => `${API}/QAReport/certificate/${qaNumber}`,
  },

  // ── Alerts — TODO: No backend controller yet ──
  alerts: {
    /** GET ?name= */
    list: `${API}/Alert`,
    /** GET api/Alert/{id} */
    byId: (id: string) => `${API}/Alert/${id}`,
    /** PUT body: UpdateAlertDTO { subject, body } */
    update: (id: string) => `${API}/Alert/${id}`,
    /** GET/POST api/Alert/{alertId}/recipients */
    recipients: (alertId: string) => `${API}/Alert/${alertId}/recipients`,
    /** DELETE api/Alert/{alertId}/recipients/{recipientId} */
    deleteRecipient: (alertId: string, recipientId: string) =>
      `${API}/Alert/${alertId}/recipients/${recipientId}`,
  },

  // ── Page feedback — served by the Azure Functions feedback API (see docs/feedback.md) ──
  feedback: {
    /** GET ?pageKey= → FeedbackComment[]; POST body: { pageKey, text } → FeedbackComment */
    list: `${API}/Feedback`,
    /** GET ?pageKey= → { count } */
    count: `${API}/Feedback/count`,
    /** GET ?query= → FeedbackPerson[] (at most 10, prefix match) */
    people: `${API}/Feedback/people`,
  },

  // ══════════════════════════════════════════════════════
  // PackagingSlipController → [Route("api/PackagingSlip")]
  // ══════════════════════════════════════════════════════
  packaging: {
    /** POST body: PackageDTO → COA packaging slips */
    list: `${API}/PackagingSlip/getCOAPkgSlip`,
    /** POST body: PackageSearchDTO → { items, totalCount } for the List Packages grid */
    searchPaged: `${API}/PackagingSlip/search/paged`,
    /** GET ?OId={Guid} */
    byId: `${API}/PackagingSlip/MyPackagesByOId`,
    /** POST body: AddSlipDTO */
    create: `${API}/PackagingSlip/AddSlip`,
    /** GET — all packages */
    getAll: `${API}/PackagingSlip/AllPackages`,
    /** GET ?lotNumber=&prodId=&packagerId= */
    myPackages: `${API}/PackagingSlip/MyPackages`,
    /** GET ?pkgSlipId={Guid} → vendor lots */
    vendorLots: `${API}/PackagingSlip/GetVendorLotsByPkgId`,
    /** POST ?pkgSlipId=&lotNumber=&PONumber=&currentUser= → appends one source vendor lot */
    addVendorLot: `${API}/PackagingSlip/AddVendorLotNumber`,
    /** PUT body: UpdateLotNumberDTO */
    updateLotNumber: `${API}/PackagingSlip/UpdateLotNumber`,
  },

  // ══════════════════════════════════════════════════════
  // CatLevelController → [Route("api/CatLevel")]
  // ══════════════════════════════════════════════════════
  categoryLevels: {
    /** GET — all category levels */
    list: `${API}/CatLevel/GetAll`,
    /** GET — all enabled */
    listEnabled: `${API}/CatLevel/GetAllEnabled`,
    /** GET api/CatLevel/GetById/{Oid} */
    byId: (oid: string) => `${API}/CatLevel/GetById/${oid}`,
    /** POST body: AddCatLevelDTO */
    create: `${API}/CatLevel/Add`,
    /** PUT body: UpdateCatLevelDTO */
    update: `${API}/CatLevel/UpdateCatLevelDropdown`,
    /** GET — active VQCA category levels */
    activeVQCA: `${API}/CatLevel/GetActiveVQCA`,
    /** GET — all distinct level names */
    distinctLevelNames: `${API}/CatLevel/GetAllDistinctLevelName`,
    /** GET api/CatLevel/GetVQCAByLabId/{labId} */
    byLabId: (labId: string) => `${API}/CatLevel/GetVQCAByLabId/${labId}`,
    /**
     * GET api/CatLevel/GetEnabledByLabId/{labId} — legacy CatLevel.GetCategoryLevelByLabId, the
     * QA Lab "Sample Type" list. Unlike byLabId it does not filter on IsVQCACat.
     */
    enabledByLabId: (labId: string) => `${API}/CatLevel/GetEnabledByLabId/${labId}`,
    /** GET ?Name= — WorkloadRptLookup turnaround days; omit Name for every category */
    lookUpDays: `${API}/CatLevel/GetLookUpDaysByName`,
  },

  // ══════════════════════════════════════════════════════
  // ExperimentController HDFX endpoints → [Route("api/Experiment")]
  // ══════════════════════════════════════════════════════
  hdfx: {
    /** POST body: QAExperimentResultSetForHDFXDTO → QA data */
    list: `${API}/Experiment/GetQAExperimentResultSetForHDFX_New`,
    /** POST body: QAExperimentResultSetForHDFXDTO → COA data */
    byProductVendor: `${API}/Experiment/GetCOAExperimentResultSetForHDFX_New`,
    /** POST body: QAExperimentResultSetForHDFXDTO → QA overall SPC calc */
    overallCalc: `${API}/Experiment/GetQAExperimentResultSetForHDFX_OverallCalc`,
    /** POST body: QAExperimentResultSetForHDFXDTO → QA yearly SPC calc */
    yearlyCalc: `${API}/Experiment/GetQAExperimentResultSetForHDFX_YearlyCalc`,
    /** POST body: QAExperimentResultSetForHDFXDTO → QA overall graph */
    overallGraph: `${API}/Experiment/GetQAExperimentResultSetForHDFX_OverallGraph`,
    /** POST body: QAExperimentResultSetForHDFXDTO → QA yearly graph */
    yearlyGraph: `${API}/Experiment/GetQAExperimentResultSetForHDFX_YearlyGraph`,
    /** POST body: QAExperimentResultSetForHDFXDTO → COA overall SPC calc */
    coaOverallCalc: `${API}/Experiment/GetCOAExperimentResultSetForHDFX_OverallCalc`,
    /** POST body: QAExperimentResultSetForHDFXDTO → COA yearly SPC calc */
    coaYearlyCalc: `${API}/Experiment/GetCOAExperimentResultSetForHDFX_YearlyCalc`,
    /** POST body: QAExperimentResultSetForHDFXDTO → COA overall graph */
    coaOverallGraph: `${API}/Experiment/GetCOAExperimentResultSetForHDFX_OverallGraph`,
    /** POST body: QAExperimentResultSetForHDFXDTO → COA yearly graph */
    coaYearlyGraph: `${API}/Experiment/GetCOAExperimentResultSetForHDFX_YearlyGraph`,
    // Lookups served by Product controller
    /** GET ?productId=&productName= → distinct VQCA vendors for HDFX (no status filter) */
    vendors: `${API}/Experiment/GetHDFXVendors`,
    /** GET ?productId=&productName=&catLevelName= → distinct STP specification names */
    specifications: `${API}/Experiment/GetHDFXSpecifications`,
    export: `${API}/Experiment/GetQAExperimentResultSetForHDFX_New`, // TODO: no export endpoint
  },

  // ── Reports — legacy Web.sitemap report execution ──
  reports: {
    list: `${API}/reports/names`,
    run: `${API}/reports/run`,
  },

  // ── SAP — TODO: No backend controller yet ──
  sap: {
    shipped: `${API}/sap/shipped/list`,                      // TODO: not implemented
    datamart: `${API}/sap/datamart/list`,                    // TODO: not implemented
    materials: (pid: string) => `${API}/sap/materials/${pid}`, // TODO: not implemented
    createMaterial: `${API}/sap/materials`,                   // TODO: not implemented
    deleteMaterial: (id: number) => `${API}/sap/materials/${id}`, // TODO: not implemented
    poLog: `${API}/sap/po-log`,                               // TODO: not implemented
    shipVsCoa: `${API}/sap/reports/ship-vs-coa`,               // TODO: not implemented
    missingPo: `${API}/sap/reports/missing-po`,                // TODO: not implemented
    monthlyMissingPo: `${API}/sap/reports/monthly-missing-po`, // TODO: not implemented
  },

  // ══════════════════════════════════════════════════════
  // Admin — maps to MemberController [Route("api/Member")]
  //         + UserRolesController [Route("api/UserRoles")]
  // ══════════════════════════════════════════════════════
  admin: {
    /** GET ?search=&userName=&email=&psl=&vendorId=&active=&portal= */
    users: `${API}/Member/users`,
    /** GET ?networkId=&email= — corporate directory user lookup */
    directoryUser: `${API}/Member/users/directory`,
    /** GET api/Member/users/{userId} */
    userById: (userId: string) => `${API}/Member/users/${userId}`,
    /** GET ?userName=&company=&firstName=&lastName=&email=&city=&countryId=&psl=&roles=&activeOnly=
     *  Manage Vendors and Vendor's Users search (QCDOC-VENDOR portal). */
    vendorUsers: `${API}/Member/users/vendors`,
    /** POST body: CreateInternalUserDTO */
    createInternalUser: `${API}/Member/users/internal`,
    /** PUT body: UpdateUserProfileDTO */
    userProfile: (userId: string) => `${API}/Member/users/${userId}/profile`,
    /** PUT body: ReplaceUserRolesDTO */
    userRolesForUser: (userId: string) => `${API}/Member/users/${userId}/roles`,
    /** POST/DELETE one role assignment */
    userRole: (userId: string, roleId: string) => `${API}/Member/users/${userId}/roles/${roleId}`,
    /** PUT body: UpdateUserStatusDTO */
    userStatus: (userId: string) => `${API}/Member/users/${userId}/status`,
    /** PUT body: ChangeUserPasswordDTO */
    userPassword: (userId: string) => `${API}/Member/users/${userId}/password`,
    /** PUT body: ChangeUserPasswordQuestionDTO */
    userPasswordQuestion: (userId: string) => `${API}/Member/users/${userId}/password-question`,
    /** GET legacy profile data, including secondary contact fields */
    legacyUserProfile: `${API}/Member/GetProfileByUserId`,
    /** GET all QCDOC roles */
    userRoles: `${API}/UserRoles/GetAll`,
    /** GET ?PSLID= */
    userRolesByPsl: `${API}/UserRoles/GetByPSL`,
    /** POST/PUT role DTOs */
    createUserRole: `${API}/UserRoles`,
    updateUserRole: `${API}/UserRoles`,
    /** GET/POST/PUT vendor roles */
    vendorRoles: `${API}/VendorRoles`,
    /** GET/PUT persisted page-role access contracts */
    manageAccess: `${API}/ManageAccess`,
    manageAccessById: (id: number) => `${API}/ManageAccess/${id}`,
  },

  // ══════════════════════════════════════════════════════
  // Vendor Management — maps to PackagerController, VQCAController, COAController
  // ══════════════════════════════════════════════════════
  vendorMgmt: {
    /** GET /api/VendorCompany/GetAll — all vendor companies */
    vendorCompanies: `${API}/VendorCompany/GetAll`,
    /** GET ?oid=&packagerId=&vendorId= → packager vendors */
    packagerVendors: `${API}/Packager/getPackagerVendors`,
    productAssignments: `${API}/Packager/productAssignments`,
    eligiblePackagingProducts: `${API}/Packager/eligibleProducts`,
    /** GET ?packagerId=&vendorId= → products the packager may package for that vendor */
    packagerAssignedProducts: `${API}/Packager/assignedProducts`,
    /** POST ?vendorId=&currentUser=&packagerId= → add packager vendor */
    addPackagerVendor: `${API}/Packager`,
    /** DELETE ?Id={Guid} → remove packager vendor */
    removePackagerVendor: `${API}/Packager/PackagerVendor`,
    /** GET ?vendorName={string} → search vendor by name */
    searchVendor: `${API}/Packager/getVendor`,
    // TODO: profiles, hierarchy, vendorUsers, vendorLots, manageData — no backend
  },

  // ══════════════════════════════════════════════════════
  // Vendor Portal — maps to VQCA, COA, Packaging controllers with vendor filters
  // ══════════════════════════════════════════════════════
  vendorPortal: {
    /** GET api/VQCA/vendor/{vendorId} → vendor VQCAs */
    vqcaList: (vendorId: string) => `${API}/VQCA/vendor/${vendorId}`,
    /** GET api/VQCA/pending/{vendorId} → VQCAs awaiting vendor signature */
    vqcaPending: (vendorId: string) => `${API}/VQCA/pending/${vendorId}`,
    /** GET api/VQCA/active/{vendorId} → APPROVED VQCAs (COA product list) */
    vqcaActive: (vendorId: string) => `${API}/VQCA/active/${vendorId}`,
    /** GET api/VQCA/details/{oid}?vqcaId= → product specification rows */
    vqcaSpecs: (oid: string) => `${API}/VQCA/details/${oid}`,
    /** POST body: VendorSignVQCADTO → sign a VQCA */
    vqcaSign: `${API}/VQCA/sign`,
    /** GET ?VQCAId={Guid} → VQCA sign logs */
    vqcaSignLogs: `${API}/VQCA/get-signlog`,
    /** POST body: COAByVendorDTO + StartIndex/MaximumRows/SORTBY → the caller's own COAs */
    coaList: `${API}/COA/vendor/list`,
    /** POST body: COAByVendorDTO → total rows behind coaList, for the pager */
    coaCount: `${API}/COA/vendor/count`,
    /** POST body: COAByVendorDTO → un-paged rows for "Export to Excel" */
    coaListExport: `${API}/COA/vendor/list/export`,
    /** GET api/COA/{coaId} */
    coaDetail: (coaId: string) => `${API}/COA/${coaId}`,
    /** POST body: AddVendorCOARequestDTO → vendor COA submission */
    coaCreate: `${API}/COA/AddVendorCOA`,
    /** GET ?lotNum=&vqcaId= → check duplicate COA */
    coaCheckDuplicate: `${API}/COA/DuplicateCOAExists`,
    /** POST body: PackageDTO → vendor packaging */
    packagingList: `${API}/PackagingSlip/getCOAPkgSlip`,
    /** POST body: AddSlipDTO → create a vendor packaging slip */
    packagingCreate: `${API}/PackagingSlip/AddSlip`,
    /** GET ?OId={Guid} → packaging slip by id */
    packagingDetail: `${API}/PackagingSlip/MyPackagesByOId`,
    /** GET ?pkgSlipId={Guid} → source vendor lots for a slip */
    packagingVendorLots: `${API}/PackagingSlip/GetVendorLotsByPkgId`,
  },

  // ── File Management — TODO: No backend controller yet ──
  files: {
    upload: `${API}/file/upload`,    // TODO: not implemented
    download: `${API}/file/download`, // TODO: not implemented
    sasUrl: `${API}/file/sas`,        // TODO: not implemented
  },

  // ── Search — TODO: No backend controller yet ──
  search: {
    query: `${API}/search`, // TODO: not implemented
  },

  // ══════════════════════════════════════════════════════
  // Lookups — maps to individual controllers
  // ══════════════════════════════════════════════════════
  lookup: {
    /** GET api/Country/GetEnabled */
    countries: `${API}/Country/GetEnabled`,
    /** GET — active states for address dropdowns */
    states: `${API}/Country/States`,
    /** GET api/PSL/activePSL */
    psls: `${API}/PSL/activePSL`,
    /** GET api/CatLevel/GetAllEnabled */
    categoryLevels: `${API}/CatLevel/GetAllEnabled`,
    /** GET ?ProductId=&ProdcutName=&PslId= */
    suppliers: `${API}/Product/getApprovedSuppliers`,
    /** POST api/ProductType/getEnabledProdTypes */
    productTypes: `${API}/ProductType/getEnabledProdTypes`,
    /** GET api/RegType/GetAllRegTypes */
    regTypes: `${API}/RegType/GetAllRegTypes`,
    /** GET api/ProdSpecType */
    specTypes: `${API}/ProdSpecType`,
    /** GET api/ProdRM/GetEnabled */
    rawMaterials: `${API}/ProdRM/GetEnabled`,
    /** GET api/QALab/GetEnabled */
    qaLabs: `${API}/QALab/GetEnabled`,
    /** GET api/VendorCompany/GetAll */
    vendors: `${API}/VendorCompany/GetAll`,
  },

  // ══════════════════════════════════════════════════════
  // Additional backend controllers (reference data)
  // ══════════════════════════════════════════════════════
  /** ProdSpecController → [Route("api/ProdSpec")] */
  prodSpec: {
    /** GET ?isSaleSpec=&isPerfSpec= */
    get: `${API}/ProdSpec`,
    /** POST body: SpecDTO */
    getByFilters: `${API}/ProdSpec/getByFilters`,
    /** POST body: SpecInsertDTO */
    create: `${API}/ProdSpec/addProdSpec`,
    /** PUT body: UpdateSpecDTO */
    update: `${API}/ProdSpec/updateProdSpec`,
    /** DELETE /api/ProdSpec/{specId} */
    delete: (specId: string) => `${API}/ProdSpec/${specId}`,
    /** GET /api/ProdSpec/audit/{specId} */
    audit: (specId: string) => `${API}/ProdSpec/audit/${specId}`,
  },

  /** ProdRMController → [Route("api/ProdRM")] */
  prodRM: {
    /** GET */
    getAll: `${API}/ProdRM/GetAll`,
    /** GET */
    getEnabled: `${API}/ProdRM/GetEnabled`,
    /** POST body: AddProdRMInfoDTO */
    create: `${API}/ProdRM/Add`,
    /** PUT body: UpdateProdRMDTO */
    update: `${API}/ProdRM/Update`,
  },

  /** PSLController → [Route("api/PSL")] */
  psl: {
    /** GET ?PSLId=&IsActive= */
    get: `${API}/PSL`,
    /** GET — active PSLs */
    active: `${API}/PSL/activePSL`,
    /** GET ?PslId={Guid} */
    byId: `${API}/PSL/byId`,
    /** POST body: PSLDTO */
    create: `${API}/PSL`,
    /** PUT body: UpdatePSLDTO */
    update: `${API}/PSL`,
  },

  /** CountryController → [Route("api/Country")] */
  country: {
    /** GET — enabled countries */
    getEnabled: `${API}/Country/GetEnabled`,
    /** GET — all countries */
    getAll: `${API}/Country/All`,
    /** POST body: CountryDTO */
    create: `${API}/Country/Add`,
    /** POST body: UpdateCountryDTO */
    update: `${API}/Country/Update`,
  },

  /** MemberController → [Route("api/Member")] */
  member: {
    /** GET ?userId={Guid} */
    profileByUserId: `${API}/Member/GetProfileByUserId`,
    /** GET */
    categoryManager: `${API}/Member/GetCategoryManager`,
    /** GET */
    categoryManagerAndAgent: `${API}/Member/GetCategoryManagerandAgent`,
    /** GET ?psls={string} */
    categoryManagerByPSL: `${API}/Member/GetCategoryManagerandAgentByPSL`,
    /** GET ?psls={string} — category managers only, purchasing agents excluded */
    categoryManagerOnlyByPSL: `${API}/Member/GetCategoryManagerByPSL`,
    /** GET ?userName=&company=&activeOnly= — vendor membership users */
    vendorUsers: `${API}/Member/GetVendorUsers`,
    /** GET ?userName=&company=&activeOnly= — internal QCDOC membership users */
    qcdocUsers: `${API}/Member/GetQCDOCUsers`,
    /** GET ?Email={string} */
    usernameByEmail: `${API}/Member/GetUsernameStartWithHel`,
    /** GET ?username={string} */
    emailByUsername: `${API}/Member/GetEmailByUsername`,
  },

  /** NewUserReqController → [Route("api/NewUserReq")] */
  newUserReq: {
    /** GET — all new user requests */
    getAll: `${API}/NewUserReq/getRequest`,
    /** POST body: NewUserReqDTO */
    getByFilter: `${API}/NewUserReq/getRequestbyFilter`,
    /** POST body: AddNewUserReqDTO */
    create: `${API}/NewUserReq`,
    /** PUT ?oId={Guid}&status={UserReqStatus}&notes={string} */
    updateStatus: `${API}/NewUserReq`,
  },

  /** PkgPONumbersController → [Route("api/PkgPONumbers")] */
  pkgPONumbers: {
    /** GET ?pkgSlipId={Guid} */
    get: `${API}/PkgPONumbers/getPkgPO`,
    /** POST ?POXML=&currentUser=&pkgSlipId= */
    addPOxml: `${API}/PkgPONumbers/AddPOxml`,
  },

  /** SecurityQuesController → [Route("api/SecurityQues")] */
  securityQues: {
    /** GET */
    getAll: `${API}/SecurityQues/GetAll`,
    /** GET */
    getEnabled: `${API}/SecurityQues/GetEnabled`,
    /** GET api/SecurityQues/{TypeId} */
    byId: (typeId: string) => `${API}/SecurityQues/${typeId}`,
    /** POST body: SecurityQuesDTO */
    create: `${API}/SecurityQues`,
    /** PUT body: UpdateSecurityQuesDTO */
    update: `${API}/SecurityQues`,
  },
} as const;
